<script setup>
import { ref, computed } from 'vue';
import ItemsPerPageSelect from '@/components/filters/ItemsPerPageSelect.vue';
import { Pagination as ShadcnPagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
    // Pagination data
    currentPage: {
        type: Number,
        required: true,
        description: 'Current page (0-based)'
    },
    pageSize: {
        type: Number,
        required: true,
        description: 'Number of items per page'
    },
    totalItems: {
        type: Number,
        required: true,
        description: 'Total number of items'
    },
    currentPageItemsCount: {
        type: Number,
        required: true,
        description: 'Number of items on current page'
    },
    filteredCount: {
        type: Number,
        default: null,
        description: 'Number of filtered items (if different from currentPageItemsCount)'
    },

    // Display configuration
    itemType: {
        type: String,
        required: true,
        description: 'Type of items being paginated (e.g., "sources", "destinations", "topics")'
    },
    loading: {
        type: Boolean,
        default: false,
        description: 'Whether pagination controls should be disabled'
    },

    // Page size configuration
    pageSizeOptions: {
        type: Array,
        default: () => [10, 25, 50, 100],
        description: 'Available page size options'
    },

    // Pagination behavior
    siblingCount: {
        type: Number,
        default: 1,
        description: 'Number of sibling pages to show around current page'
    },

    // Display options
    showItemsPerPage: {
        type: Boolean,
        default: true,
        description: 'Whether to show the items per page selector'
    },
    showTotalItems: {
        type: Boolean,
        default: true,
        description: 'Whether to show total items count in the showing text'
    },
    hasNext: {
        type: Boolean,
        default: null,
        description: 'Whether there are more pages available (used when totalItems is not reliable)'
    }
});

const emit = defineEmits(['page-change', 'page-size-change']);

const directPageInput = ref('');

const isFiltered = computed(() => {
    return props.filteredCount !== null && props.filteredCount !== props.currentPageItemsCount;
});

const showingText = computed(() => {
    if (props.loading) return '';

    if (isFiltered.value) {
        return `Showing ${props.filteredCount} ${props.filteredCount === 1 ? 'match' : 'matches'} on this page`;
    }

    const startItem = props.currentPage * props.pageSize + 1;
    const endItem = props.currentPage * props.pageSize + props.currentPageItemsCount;

    if (props.showTotalItems) {
        return `Showing ${props.itemType} ${startItem} to ${endItem} of ${props.totalItems}`;
    } else {
        return `Showing ${props.itemType} ${startItem} to ${endItem}`;
    }
});

const totalPages = computed(() => {
    if (!props.totalItems || props.totalItems <= 0 || !props.pageSize || props.pageSize <= 0) {
        return 1;
    }
    const calculated = Math.ceil(props.totalItems / props.pageSize);
    return Math.max(1, calculated);
});

const canGoNext = computed(() => {
    if (props.hasNext !== null) {
        return props.hasNext;
    }
    return props.currentPage < totalPages.value - 1;
});

const canGoPrevious = computed(() => {
    return props.currentPage > 0;
});

const getVisiblePages = () => {
    try {
        if (props.hasNext !== null) {
            return getProgressivePages();
        }

        const totalPagesValue = Math.max(1, totalPages.value);
        const currentPage = Math.max(1, props.currentPage + 1);
        const siblingCount = Math.max(0, props.siblingCount);
        const pages = [];

        if (totalPagesValue <= 0 || currentPage <= 0 || !Number.isFinite(totalPagesValue) || !Number.isFinite(currentPage)) {
            return [1];
        }

        if (totalPagesValue <= 7) {
            for (let i = 1; i <= totalPagesValue; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            const startWindow = Math.max(2, currentPage - siblingCount);
            const endWindow = Math.min(totalPagesValue - 1, currentPage + siblingCount);

            if (startWindow > 2 && startWindow > 0 && totalPagesValue > 0) {
                const leftHiddenPages = [];
                const startIdx = 2;
                const endIdx = startWindow;
            
                for (let i = startIdx; i < endIdx && i > 0; i++) {
                    leftHiddenPages.push(i);
                }
                if (leftHiddenPages.length > 0) {
                    pages.push({
                        type: 'ellipsis',
                        position: 'left',
                        range: leftHiddenPages
                    });
                }
            }

            for (let i = startWindow; i <= endWindow && i <= totalPagesValue && i > 0; i++) {
                if (i !== 1 && i !== totalPagesValue && i >= 1) {
                    pages.push(i);
                }
            }

            if (endWindow < totalPagesValue - 1 && endWindow >= 0 && totalPagesValue > 0) {
                const rightHiddenPages = [];
                const startIdx = endWindow + 1;
                const endIdx = totalPagesValue;
            
                for (let i = startIdx; i < endIdx && i > 0; i++) {
                    rightHiddenPages.push(i);
                }
                if (rightHiddenPages.length > 0) {
                    pages.push({
                        type: 'ellipsis',
                        position: 'right',
                        range: rightHiddenPages
                    });
                }
            }

            if (totalPagesValue > 1) {
                pages.push(totalPagesValue);
            }
        }

        return pages;
    } catch (error) {
        console.error('Error in getVisiblePages:', error);
        return [1];
    }
};

const getProgressivePages = () => {
    try {
        const currentPageDisplay = Math.max(1, props.currentPage + 1);
        const pages = [];
        const siblingCount = 2;

        if (!Number.isFinite(currentPageDisplay) || currentPageDisplay <= 0) {
            return [1];
        }

        if (currentPageDisplay <= 5) {
            for (let i = 1; i <= currentPageDisplay; i++) {
                pages.push(i);
            }

            if (props.hasNext) {
                pages.push(currentPageDisplay + 1);
            }
        } else {
            pages.push(1);

            const startWindow = Math.max(2, currentPageDisplay - siblingCount);
            const endWindow = currentPageDisplay + siblingCount;

            const hiddenPages = [];
            for (let i = 2; i < startWindow; i++) {
                hiddenPages.push(i);
            }

            if (hiddenPages.length > 0) {
                pages.push({
                    type: 'ellipsis',
                    position: 'progressive',
                    range: hiddenPages
                });
            }

            for (let i = startWindow; i <= Math.min(endWindow, currentPageDisplay); i++) {
                if (i !== 1) {
                    pages.push(i);
                }
            }

            if (props.hasNext) {
                pages.push(currentPageDisplay + 1);
            }
        }

        return pages;
    } catch (error) {
        console.error('Error in getProgressivePages:', error);
        return [1];
    }
};

const getDropdownPages = (ellipsisInfo) => {
    if (!ellipsisInfo || !ellipsisInfo.range) {
        return [];
    }

    return ellipsisInfo.range.sort((a, b) => a - b).map(page => ({
        id: page,
        page: page,
        size: 32
    }));
};

const getProgressiveDropdownPages = () => {
    const currentPageDisplay = props.currentPage + 1;
    const pages = [];
    
    for (let i = 2; i < currentPageDisplay - 2; i++) {
        if (i > 1) {
            pages.push({
                id: i,
                page: i,
                size: 32
            });
        }
    }
    
    return pages;
};

const onPageChange = (newPage) => {
    if (newPage < 0) return;

    if (props.hasNext !== null) {
        const maxKnownPage = props.currentPage + (props.hasNext ? 1 : 0);
        if (newPage > maxKnownPage) return;
    }

    emit('page-change', newPage);
};

const onPageSizeChange = (newSize) => {
    emit('page-size-change', newSize);
};

const goToDirectPage = () => {
    const pageNumber = parseInt(directPageInput.value);
    if (pageNumber && pageNumber >= 1 && pageNumber <= totalPages.value) {
        onPageChange(pageNumber - 1);
        directPageInput.value = '';
    }
};
</script>

<template>
    <div class="pt-2">
        <div class="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
            <div class="flex items-center gap-4 min-w-0">
                <div class="text-sm text-muted-foreground whitespace-nowrap">
                    {{ showingText }}
                </div>
                <ItemsPerPageSelect
                    v-if="showItemsPerPage"
                    :model-value="pageSize"
                    :loading="loading"
                    :placeholder="`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} per page`"
                    @update:model-value="onPageSizeChange" />
            </div>
            <div class="flex justify-end">
                <!-- Traditional pagination: custom implementation with dropdown -->
                <ShadcnPagination
                    v-if="hasNext === null"
                    :page="currentPage + 1"
                    :items-per-page="pageSize"
                    :total="totalItems"
                    :disabled="loading"
                    :sibling-count="siblingCount"
                    :show-edges="true"
                    @update:page="onPageChange($event - 1)">
                    <PaginationContent>
                        <PaginationPrevious :disabled="!canGoPrevious" />
                        <template v-for="page in getVisiblePages()" :key="typeof page === 'object' ? `ellipsis-${page.position}` : page">
                            <PaginationItem
                                v-if="typeof page === 'number'"
                                :value="page"
                                :is-active="page === currentPage + 1"
                                :class="page >= 1000 ? 'min-w-12 px-3' : page >= 100 ? 'min-w-10 px-2' : ''">
                                {{ page }}
                            </PaginationItem>
                            <DropdownMenu v-else-if="typeof page === 'object' && page.type === 'ellipsis'">
                                <DropdownMenuTrigger as-child>
                                    <button
                                        class="flex h-9 w-9 items-center justify-center hover:bg-accent hover:text-accent-foreground rounded-md transition-colors">
                                        …
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="center" class="w-48 max-h-80">
                                    <!-- Direct page input section -->
                                    <div class="p-2 border-b border-border">
                                        <div class="flex gap-1">
                                            <input
                                                v-model="directPageInput"
                                                type="number"
                                                :min="1"
                                                :max="totalPages"
                                                placeholder="Page"
                                                class="flex-1 h-7 px-2 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
                                                @keydown.enter="goToDirectPage"
                                                @focus="$event.target.select()" />
                                            <Button
                                                @click="goToDirectPage"
                                                size="sm"
                                                class="h-7 px-2 text-xs">
                                                Go
                                            </Button>
                                        </div>
                                        <div class="text-xs text-muted-foreground mt-1">
                                            of {{ totalPages }} pages
                                        </div>
                                    </div>
                                    
                                    <!-- Scrollable page list -->
                                    <div class="max-h-48">
                                        <RecycleScroller
                                            v-if="getDropdownPages(page).length > 0"
                                            :items="getDropdownPages(page)"
                                            key-field="id"
                                            size-field="size"
                                            :default-size="32"
                                            class="max-h-48">
                                            <template #default="{ item }">
                                                <DropdownMenuItem
                                                    @click.prevent="onPageChange(item.page - 1)"
                                                    class="cursor-pointer justify-center h-8 flex items-center">
                                                    {{ item.page }}
                                                </DropdownMenuItem>
                                            </template>
                                        </RecycleScroller>
                                        <div v-else class="px-2 py-1.5 text-sm text-muted-foreground">
                                            No other pages
                                        </div>
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </template>
                        <PaginationNext :disabled="!canGoNext" />
                    </PaginationContent>
                </ShadcnPagination>

                <!-- Progressive pagination: custom implementation -->
                <ShadcnPagination
                    v-else
                    :page="currentPage + 1"
                    :items-per-page="pageSize"
                    :total="999999"
                    :disabled="loading"
                    :sibling-count="siblingCount"
                    :show-edges="false"
                    @update:page="onPageChange($event - 1)">
                    <PaginationContent>
                        <PaginationPrevious :disabled="!canGoPrevious" />
                        <template v-for="page in getProgressivePages()" :key="page">
                            <PaginationItem
                                v-if="page !== '...'"
                                :value="page"
                                :is-active="page === currentPage + 1"
                                :class="page >= 1000 ? 'min-w-12 px-3' : page >= 100 ? 'min-w-10 px-2' : ''">
                                {{ page }}
                            </PaginationItem>
                            <DropdownMenu v-else>
                                <DropdownMenuTrigger as-child>
                                    <button
                                        class="flex h-9 w-9 items-center justify-center hover:bg-accent hover:text-accent-foreground rounded-md transition-colors">
                                        …
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="center" class="w-48 max-h-80">
                                    <!-- Direct page input section -->
                                    <div class="p-2 border-b border-border">
                                        <div class="flex gap-1">
                                            <input
                                                v-model="directPageInput"
                                                type="number"
                                                :min="1"
                                                :max="totalPages"
                                                placeholder="Page"
                                                class="flex-1 h-7 px-2 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
                                                @keydown.enter="goToDirectPage"
                                                @focus="$event.target.select()" />
                                            <Button
                                                @click="goToDirectPage"
                                                size="sm"
                                                class="h-7 px-2 text-xs">
                                                Go
                                            </Button>
                                        </div>
                                        <div class="text-xs text-muted-foreground mt-1">
                                            of {{ totalPages }} pages
                                        </div>
                                    </div>
                                    
                                    <!-- Scrollable page list -->
                                    <div class="max-h-48">
                                        <RecycleScroller
                                            v-if="getProgressiveDropdownPages().length > 0"
                                            :items="getProgressiveDropdownPages()"
                                            key-field="id"
                                            size-field="size"
                                            :default-size="32"
                                            class="max-h-48">
                                            <template #default="{ item }">
                                                <DropdownMenuItem
                                                    @click.prevent="onPageChange(item.page - 1)"
                                                    class="cursor-pointer justify-center h-8 flex items-center">
                                                    {{ item.page }}
                                                </DropdownMenuItem>
                                            </template>
                                        </RecycleScroller>
                                        <div v-else class="px-2 py-1.5 text-sm text-muted-foreground">No other pages</div>
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </template>
                        <PaginationNext :disabled="!canGoNext" />
                    </PaginationContent>
                </ShadcnPagination>
            </div>
        </div>
    </div>
</template>